import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createSession, getSessionStatus } from '@services/sessionService';
import { pruneAnalyticsLedger } from '@hooks/useAnalytics/useAnalytics';
import { getUtmParams } from '@utils/utmParams';
import { useSessionStorage } from '@hooks/useSessionStorage';
import type { SessionContextValue } from '@t/session.types';

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const {
    storedSessionId,
    storedResumeToken,
    currentStep,
    expiresAt,
    hasValidSession,
    persistSession,
    setSession,
    updateFromResume,
    clearSessionData,
    clearRouteData,
    setCurrentStep,
    setHasValidSession,
    isStoredSessionFresh,
  } = useSessionStorage();

  const [isInitializing, setIsInitializing] = useState(true);

  // Capture UTM params once on mount (before any navigation changes the URL)
  const utmParams = useMemo(() => getUtmParams(window.location.search), []);

  // Guard so the startup validation only fires once
  const didValidate = useRef(false);

  // On mount: validate stored session or create a new one
  useEffect(() => {
    if (didValidate.current) return;
    didValidate.current = true;

    const initSession = async () => {
      // Check if we have a fresh stored session
      if (storedSessionId && isStoredSessionFresh()) {
        try {
          const status = await getSessionStatus(storedSessionId);
          if (status.status !== 'COMPLETED' && status.status !== 'EXPIRED') {
            setCurrentStep(status.currentStep);
            setHasValidSession(true);
            setIsInitializing(false);
            return;
          }
        } catch {
          // Backend unreachable or 404 → fall through to create new session
        }
        // Session expired or completed on backend → full cleanup including route
        clearSessionData();
        clearRouteData();
      } else if (storedSessionId && !isStoredSessionFresh()) {
        // Local TTL expired → full cleanup including route
        clearSessionData();
        clearRouteData();
      }

      // No valid session → create a new one (keep saved route for completed-order pages)
      try {
        const session = await createSession(utmParams);
        persistSession(session);
      } catch {
        // Session creation failed — will be retried via ensureSession later
      } finally {
        setIsInitializing(false);
      }
    };

    initSession();
  }, []);

  // Drop old-session ledger entries — they can never dedupe anything again and would grow localStorage without bound.
  useEffect(() => {
    if (!storedSessionId) return;
    pruneAnalyticsLedger(storedSessionId);
  }, [storedSessionId]);

  // Ref to hold a pending ensureSession promise so concurrent calls share it
  const ensurePromiseRef = useRef<Promise<string> | null>(null);

  const ensureSession = useCallback(async (): Promise<string> => {
    // Already have a valid session → return immediately
    if (storedSessionId && hasValidSession) {
      return storedSessionId;
    }

    // If another call is already in-flight, share the promise
    if (ensurePromiseRef.current) {
      return ensurePromiseRef.current;
    }

    const promise = (async () => {
      try {
        const session = await createSession(utmParams);
        persistSession(session);
        return session.sessionId;
      } finally {
        ensurePromiseRef.current = null;
      }
    })();

    ensurePromiseRef.current = promise;
    return promise;
  }, [storedSessionId, hasValidSession, persistSession, utmParams]);

  const value: SessionContextValue = {
    sessionId: storedSessionId,
    resumeToken: storedResumeToken,
    currentStep,
    expiresAt,
    isInitializing,
    hasValidSession,
    setSession,
    updateFromResume,
    clearSession: clearSessionData,
    ensureSession,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSessionContext = (): SessionContextValue => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within SessionProvider');
  }
  return context;
};
